import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import { ArrowLeft, ShieldCheck, ShieldOff, Square, Trash2 } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  useConfig,
  useDeleteSession,
  useScenarios,
  useScenario,
  useSession,
  useStopSession,
  useTranscript,
} from '@/api/queries'
import { ConfirmDialog } from '@/components/ui/dialog'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Meter,
  Skeleton,
  complianceTone,
} from '@/components/ui/primitives'
import { AiReview } from '@/features/sessions/ai-review'
import { AudioPlayer, type PlayerHandle } from '@/features/sessions/audio-player'
import { SessionStatusBadge } from '@/features/sessions/status-badge'
import { Transcript } from '@/features/sessions/transcript'
import { formatDateTime, formatDuration, formatPercent, formatSentiment } from '@/shared/format'
import { METRIC_CAVEATS, apiErrorLabel, sourceLabel } from '@/shared/labels'
import { canDeleteSessions } from '@/shared/permissions'

export const Route = createFileRoute('/_auth/sessions/$sessionId')({
  component: SessionDetailPage,
})

function SessionDetailPage() {
  const { sessionId } = Route.useParams()
  const { me } = Route.useRouteContext()
  const router = useRouter()
  const config = useConfig()

  const [currentTimeMs, setCurrentTimeMs] = useState(0)
  const playerRef = useRef<PlayerHandle | null>(null)
  const [confirmStop, setConfirmStop] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const session = useSession(sessionId, {
    // Идущий разговор обновляем сами: статус и метрики меняются на глазах.
    refetchInterval: (query) => (query.state.data?.status === 'active' ? 10_000 : false),
  })
  const isActive = session.data?.status === 'active'
  const transcript = useTranscript(sessionId, { refetchInterval: isActive ? 5000 : undefined })

  // Стоп-слова и ключевые слова для подсветки берём из активного сценария:
  // в сессии хранится script_id, но API отдаёт только текущую версию сценария.
  const scenarios = useScenarios()
  const activeScenarioId = scenarios.data?.find((item) => item.is_active)?.id
  const activeScenario = useScenario(activeScenarioId ?? '')

  const stopSession = useStopSession()
  const deleteSession = useDeleteSession()

  const handlePlayerReady = useCallback((handle: PlayerHandle) => {
    playerRef.current = handle
  }, [])
  const handleTimeUpdate = useCallback((seconds: number) => {
    setCurrentTimeMs(seconds * 1000)
  }, [])
  const handleSeek = useCallback((seconds: number) => {
    playerRef.current?.seekTo(seconds)
  }, [])

  const metrics = session.data?.metrics
  const compliancePercent = useMemo(
    () => (metrics?.script_compliance == null ? null : metrics.script_compliance * 100),
    [metrics],
  )

  if (session.isError) {
    return (
      <Card>
        <ErrorState
          message={apiErrorLabel(
            (session.error as { detail?: unknown }).detail,
            'Не удалось загрузить разговор',
          )}
          onRetry={() => void session.refetch()}
        />
      </Card>
    )
  }

  if (!session.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  const data = session.data

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/sessions"
            className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] text-ink-muted transition-colors hover:text-ink"
          >
            <ArrowLeft className="size-3.5" />
            Все разговоры
          </Link>
          <h1 className="mt-1.5 font-display text-xl font-bold text-ink">
            Разговор {formatDateTime(data.started_at)}
          </h1>
          <p className="mt-0.5 text-[13px] text-ink-subtle">
            {data.employee_full_name ?? 'Сотрудник не найден'} · {sourceLabel(data.source)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SessionStatusBadge status={data.status} />
          {data.consent_flag ? (
            <Badge tone="positive">
              <ShieldCheck className="size-3.5" />
              Согласие получено
            </Badge>
          ) : (
            <Badge tone="warning">
              <ShieldOff className="size-3.5" />
              Согласие не отмечено
            </Badge>
          )}
          {isActive && (
            <Button size="sm" variant="danger" onClick={() => setConfirmStop(true)}>
              <Square className="size-3.5" />
              Прервать
            </Button>
          )}
          {canDeleteSessions(me) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              title="Удалить разговор"
            >
              <Trash2 className="size-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <div className="space-y-4">
          {data.audio_uri ? (
            <AudioPlayer
              sessionId={data.id}
              onTimeUpdate={handleTimeUpdate}
              onReady={handlePlayerReady}
            />
          ) : (
            <Card className="p-4">
              <p className="text-[13px] text-ink-subtle">
                {isActive
                  ? 'Запись появится после завершения разговора.'
                  : 'Аудиозапись для этого разговора недоступна.'}
              </p>
            </Card>
          )}

          <Transcript
            utterances={transcript.data ?? []}
            loading={transcript.isLoading}
            currentTimeMs={currentTimeMs}
            definition={activeScenario.data?.definition ?? null}
            onSeek={handleSeek}
          />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Метрики" />
            {metrics ? (
              <dl className="divide-y divide-line/60">
                <MetricRow label="Соблюдение скрипта">
                  <div className="flex items-center gap-2.5">
                    <span className="font-mono text-[13px] text-ink">
                      {formatPercent(metrics.script_compliance)}
                    </span>
                    {compliancePercent != null && (
                      <Meter
                        value={compliancePercent}
                        tone={complianceTone(compliancePercent)}
                        className="w-16"
                      />
                    )}
                  </div>
                </MetricRow>
                <MetricRow label="Тональность" caption="Шкала от -1 до 1">
                  <span className="font-mono text-[13px] text-ink">
                    {formatSentiment(metrics.sentiment_score)}
                  </span>
                </MetricRow>
                <MetricRow label="Перебивания" caption={METRIC_CAVEATS.interruptions}>
                  <span className="font-mono text-[13px] text-ink">{metrics.interruptions}</span>
                </MetricRow>
                <MetricRow label="Доля времени с речью" caption={METRIC_CAVEATS.talkRatio}>
                  <span className="font-mono text-[13px] text-ink">
                    {formatPercent(metrics.talk_ratio)}
                  </span>
                </MetricRow>
                <MetricRow label="Длительность">
                  <span className="font-mono text-[13px] text-ink">
                    {formatDuration(metrics.duration_sec)}
                  </span>
                </MetricRow>
                <MetricRow label="Окончание">
                  <span className="text-[13px] text-ink">{formatDateTime(data.ended_at)}</span>
                </MetricRow>
              </dl>
            ) : (
              <p className="px-5 py-4 text-[13px] text-ink-subtle">
                Метрики ещё не посчитаны: разговор не завершён или во время него не было активного
                сценария.
              </p>
            )}
          </Card>

          <AiReview session={data} user={me} llmEnabled={config.data?.llm_enabled ?? false} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmStop}
        onOpenChange={setConfirmStop}
        title="Прервать этот разговор?"
        description="Запись остановится, аудио и расшифровка сохранятся."
        confirmLabel="Прервать"
        destructive
        loading={stopSession.isPending}
        onConfirm={() =>
          stopSession.mutate(data.id, {
            onSuccess: () => {
              toast.success('Разговор остановлен')
              setConfirmStop(false)
            },
            onError: (error) => toast.error(apiErrorLabel((error as { detail?: unknown }).detail)),
          })
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Удалить этот разговор?"
        description="Разговор скроется из списков и отчётов. Запись и расшифровка останутся в хранилище."
        confirmLabel="Удалить"
        destructive
        loading={deleteSession.isPending}
        onConfirm={() =>
          deleteSession.mutate(data.id, {
            onSuccess: () => {
              toast.success('Разговор удалён')
              void router.navigate({ to: '/sessions' })
            },
            onError: (error) => toast.error(apiErrorLabel((error as { detail?: unknown }).detail)),
          })
        }
      />
    </div>
  )
}

function MetricRow({
  label,
  caption,
  children,
}: {
  label: string
  caption?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-3 px-5 py-2.5">
      <dt className="min-w-0">
        <span className="text-[13px] text-ink-muted">{label}</span>
        {caption && <span className="block text-xs text-ink-subtle">{caption}</span>}
      </dt>
      <dd className="shrink-0">{children}</dd>
    </div>
  )
}
